import React, { useState, useEffect } from "react";
import "./CommonTable.css";
import {Button} from 'react-bootstrap';
import { useTable, usePagination, useRowSelect, useFilters} from "react-table";
import * as GlobalConst from "../../../components/AscConstants";


const IndeterminateCheckbox = React.forwardRef(
    ({ indeterminate, ...rest }, ref) => {
        const defaultRef = React.useRef();
        const resolvedRef = ref || defaultRef;

        React.useEffect(() => {
            resolvedRef.current.indeterminate = indeterminate;
        }, [resolvedRef, indeterminate]);

        return (
            <>
                <input type="checkbox" className="form-check-input" ref={resolvedRef} {...rest} />
            </>
        );
    }
);

function DefaultColumnFilter({
                                 column: { filterValue, setFilter },
                             }) {

    return (
        <input
            className="filter-input"
            value={filterValue || ''}
            onChange={e => {
                setFilter(e.target.value || undefined)
            }}
        />
    )
}

function Table( {columns, data, state, loading, pageCount: controlledPageCount, propsPageIndex, ...props} ){
    // 動的高さ計算のためのstate
    const [containerHeight, setContainerHeight] = useState(props.tableHeight || 400);
    const [bodyHeight, setBodyHeight] = useState(props.tbodyHeight || 350);
    const resizeTimeoutRef = React.useRef(null);

    // ウィンドウリサイズ時の高さ再計算（debounce）
    // props で高さが指定されている場合は内部計算を無効化
    useEffect(() => {
        if (props.tableHeight != null || props.tbodyHeight != null) {
            return;
        }

        const calculateHeight = () => {
            const headerHeight = GlobalConst.VALUES.header_height;
            const paginationHeight = props.showPagination ? 60 : 0;
            const filterHeight = props.showFilter ? 40 : 0;
            const bottomOffset = props.tableBottomHeight ? Number(props.tableBottomHeight) : 0;
            const newTableHeight = window.innerHeight - headerHeight - GlobalConst.VALUES.table_size_tune - bottomOffset;
            const newBodyHeight = newTableHeight - 30 - paginationHeight - filterHeight;

            // 最小高さを動的に調整（データがない場合は小さく）
            const minTableHeight = data && data.length > 0 ? GlobalConst.VALUES.table_size_tune : 100;
            const minBodyHeight = data && data.length > 0 ? 100 : 80;

            setContainerHeight(Math.max(newTableHeight, minTableHeight));
            setBodyHeight(Math.max(newBodyHeight, minBodyHeight));
        };

        // 初期計算（依存変更時も即時反映）
        calculateHeight();

        // リサイズイベントリスナー（200ms debounce）
        const onResize = () => {
            if (resizeTimeoutRef.current) {
                clearTimeout(resizeTimeoutRef.current);
            }
            resizeTimeoutRef.current = setTimeout(() => {
                calculateHeight();
            }, 200);
        };

        window.addEventListener('resize', onResize);
        return () => {
            window.removeEventListener('resize', onResize);
            if (resizeTimeoutRef.current) {
                clearTimeout(resizeTimeoutRef.current);
                resizeTimeoutRef.current = null;
            }
        };
    }, [props.showPagination, props.showFilter, props.tableBottomHeight, data, props.tableHeight, props.tbodyHeight]);

    // propsが変更された時の高さ更新（props 優先。tableBottomHeight/pagination/filter を考慮）
    useEffect(() => {
        const bottomOffset = props.tableBottomHeight ? Number(props.tableBottomHeight) : 0;
        const paginationHeight = props.showPagination ? 60 : 0;
        const filterHeight = props.showFilter ? 40 : 0;
        const minTableHeight = data && data.length > 0 ? GlobalConst.VALUES.table_size_tune : 100;
        const minBodyHeight = data && data.length > 0 ? 100 : 80;

        let appliedContainerHeight = null;
        if (props.tableHeight != null) {
            appliedContainerHeight = Math.max(props.tableHeight - bottomOffset, minTableHeight);
            setContainerHeight(appliedContainerHeight);
        }
        if (props.tbodyHeight != null) {
            const adjustedTbody = Math.max(props.tbodyHeight - bottomOffset - 30 - paginationHeight - filterHeight, minBodyHeight);
            setBodyHeight(adjustedTbody);
        } else if (props.tableHeight != null) {
            const base = appliedContainerHeight != null ? appliedContainerHeight : (props.tableHeight - bottomOffset);
            const derivedBody = Math.max(base - 30 - paginationHeight - filterHeight, minBodyHeight);
            setBodyHeight(derivedBody);
        }
    }, [props.tableHeight, props.tbodyHeight, props.tableBottomHeight, props.showPagination, props.showFilter, data]);

    const filterTypes = React.useMemo(
        () => ({
            text: (rows, id, filterValue) => {
                return rows.filter(row => {
                    const rowValue = row.values[id]
                    return rowValue !== undefined
                        ? String(rowValue)
                            .toLowerCase()
                            .startsWith(String(filterValue).toLowerCase())
                        : true
                })
            }}), []
    )

    const defaultColumn = React.useMemo(
        () => ({
            Filter: DefaultColumnFilter,
        }), []
    )

    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        prepareRow,
        page,
        rows,
        canPreviousPage,
        canNextPage,
        pageOptions,
        pageCount,
        gotoPage,
        nextPage,
        previousPage,
        setPageSize,
        selectedFlatRows,
        toggleAllRowsSelected,
        state: {pageIndex, pageSize}
    } = useTable({
            columns,
            data,
            // 行IDを配列インデックスではなくエンティティID（id）基準にする。
            // これにより manualPagination でページ送りしても、選択状態が
            // 「同じインデックスの別行」へ引き継がれる不具合を防ぐ。
            // id を持たないデータの場合は従来どおりインデックスにフォールバックする。
            getRowId: (row, relativeIndex) =>
                row && row.id != null ? String(row.id) : String(relativeIndex),
            initialState: {
                pageSize: props.defaultPageSize,
                hiddenColumns: columns.map(column => {
                    if (column.show === false) return column.accessor || column.id;
                    return undefined;
                })
            },
            defaultColumn,
            filterTypes,
            manualPagination: props.manual,
            autoResetPage: props.autoResetPage !== undefined ? props.autoResetPage : (propsPageIndex !== undefined ? false : true),
            autoResetExpanded: false,
            autoResetGroupBy: false,
            autoResetSelectedRows: false,
            autoResetSortBy: false,
            autoResetFilters: false,
            autoResetRowState: false,
            pageCount: controlledPageCount,
        },
        useFilters,
        usePagination,
        useRowSelect,
        (hooks) => {
            (props.showCheckbox &&
                hooks.visibleColumns.push((columns) => [
                    {
                        id: "selection",
                        width: "20px",
                        Header: ({ getToggleAllPageRowsSelectedProps }) => (
                            <div>
                                <IndeterminateCheckbox {...getToggleAllPageRowsSelectedProps()} />
                            </div>
                        ),
                        Cell: ({ row }) => (
                            <div>
                                <IndeterminateCheckbox {...row.getToggleRowSelectedProps()} />
                            </div>
                        )
                    },
                    ...columns
                ]));
        })

    const prevPropsPageIndexRef = React.useRef(propsPageIndex);

    // Issue #452: 直前と同じ取得条件での再取得を抑止する。
    // pageIndex などが同じ値に戻る操作（入力のクランプ、propsPageIndex の同期など）で
    // 同一リクエストが重複送出されるのを防ぐ。明示的な再取得（reactTableRefresh）は
    // この effect を経由せず親が直接呼ぶため影響しない。
    const lastFetchKeyRef = React.useRef(null);

    React.useEffect(() => {
        if(!props.onFetchData) return;
        const fetchKey = JSON.stringify([pageSize, pageIndex, props.sorted, props.filtered]);
        if(lastFetchKeyRef.current === fetchKey) return;
        lastFetchKeyRef.current = fetchKey;
        props.onFetchData(pageSize, pageIndex, props.sorted, props.filtered)
    }, [props.onFetchData, pageSize, pageIndex, props.sorted, props.filtered])

    // propsPageIndexが変更された場合のみページ移動（無限ループ防止）
    React.useEffect(() => {
        if(propsPageIndex !== undefined && propsPageIndex !== null) {
            if(prevPropsPageIndexRef.current !== propsPageIndex) {
                prevPropsPageIndexRef.current = propsPageIndex;
                if(pageIndex !== propsPageIndex) {
                    gotoPage(propsPageIndex);
                }
            }
        }
    }, [propsPageIndex])

    // ページ移動時に選択状態をクリアする。
    // サーバーサイドページング（manualPagination）では別ページの選択を保持しても
    // 操作対象にできず、autoResetSelectedRows=false により選択がページ間で蓄積する。
    // その結果「チェック表示」と「実際の操作対象（selectedData）」が食い違うため、
    // ページが変わったら選択を解除し、表示と実データを常に一致させる。
    const prevPageIndexRef = React.useRef(pageIndex);
    React.useEffect(() => {
        if (prevPageIndexRef.current !== pageIndex) {
            prevPageIndexRef.current = pageIndex;
            if (props.showCheckbox) {
                toggleAllRowsSelected(false);
            }
        }
    }, [pageIndex, toggleAllRowsSelected, props.showCheckbox])

    // 選択された行の「ID集合」をキー化し、件数ではなく選択対象の変化で発火させる。
    // 件数が同じでも選択対象が変わるケース（フィルタ適用やページ間での選択蓄積など）でも
    // 親の selectedData が古いまま残らないようにする。
    // selectedFlatRows は毎レンダーで新規参照になるため依存配列には入れず、
    // 安定した文字列キー（selectedRowIdKey）を発火トリガーにする。
    //
    // 【重要】依存配列に props.onSelectedDataChange を入れないこと。
    // 呼び出し側が onSelectedDataChange={(d)=>this.setState(...)} のようにインライン関数で渡すと
    // 毎レンダー新しい参照になり、依存に含めると effect が毎レンダー発火 → 親 setState →
    // 再レンダーの無限ループ（チェック・入力が不能）になる。本コンポーネントは多数の画面で
    // インライン関数のまま使われているため、ここで依存から除外して一括防御する。
    // effect 実行時には最新の onSelectedDataChange クロージャが呼ばれるため動作上の問題はない。
    const selectedRowIdKey = selectedFlatRows.map(d => d.id).join(',');
    React.useEffect(() => {
        if(!props.showCheckbox || !props.onSelectedDataChange) return;
        props.onSelectedDataChange(selectedFlatRows.map(d => d.original));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedRowIdKey, props.showCheckbox])

    // Issue #452: ページ番号入力は onChange で即 gotoPage せず、入力中はローカル state
    // だけを更新する。onChange のたびに gotoPage すると pageIndex が変わり、上の
    // onFetchData が発火するため、↑キー長押し・スピナー長押し・ホイールの1ステップ＝
    // 1リクエストとなり、1操作で数百件が送出されていた。
    // 初期値はマウント時点の pageIndex（propsPageIndex 未指定なら 0）を見る。
    // propsPageIndex が渡されている場合はマウント直後に同期の effect が gotoPage を
    // 呼んで pageIndex が変わるが、直後の useEffect([pageIndex]) が入力欄を追従させる。
    const [pageInputValue, setPageInputValue] = useState(String(pageIndex + 1));

    // ボタン操作や propsPageIndex による外部からのページ移動を入力欄へ反映する
    useEffect(() => {
        setPageInputValue(String(pageIndex + 1));
    }, [pageIndex]);

    // Issue #452: 入力値を確定させる（onBlur / Enter）。
    // 1 〜 ページ数にクランプし、現在のページと同じなら gotoPage を呼ばない。
    const commitPageInput = () => {
        const maxPage = pageOptions.length > 0 ? pageOptions.length : 1;
        const inputNumber = Number(pageInputValue);
        if (!pageInputValue || Number.isNaN(inputNumber)) {
            // 空欄・不正値は現在のページへ戻す
            setPageInputValue(String(pageIndex + 1));
            return;
        }
        const nextPage = Math.min(Math.max(Math.trunc(inputNumber), 1), maxPage);
        setPageInputValue(String(nextPage));
        if (nextPage - 1 !== pageIndex) {
            gotoPage(nextPage - 1);
        }
    };

    return (
        <>
            <div className={`table-div${props.className ? ` ${props.className}` : ""}`} style={{
                maxHeight: containerHeight,
                minHeight: data && data.length > 0 ? '150px' : '100px',
                height: 'auto'
            }}>
            <table {...getTableProps()} className="common-table">
            <thead>
                {headerGroups.map((headerGroup) => (
                    <tr {...headerGroup.getHeaderGroupProps()}>
                        {headerGroup.headers.map((column) => (

                        <th {...column.getHeaderProps()} style={{width:column.width}}>
                            {column.render("Header")}
                            {props.showFilter&&
                                <div >{column.canFilter ? column.render('Filter') : null}</div>
                            }
                        </th>
                        ))}
                    </tr>
                ))}
            </thead>
            <tbody {...getTableBodyProps()} style={{
                maxHeight: bodyHeight,
                minHeight: '150px'
            }}>
                {page.map((row, i) => {
                prepareRow(row);
                return (
                    <tr {...row.getRowProps()}>
                    {row.cells.map((cell) => {
                        return (
                        <td {...cell.getCellProps()}>{cell.render("Cell")}</td>
                        );
                    })}
                    </tr>
                );
                })}
            </tbody>
            </table>
            {
                loading && !rows.length
                ? <div className="table-loading">{props.langText && props.langText.LoadingText ? props.langText.LoadingText : '読み込み中...'}</div>
                : (!rows.length
                    ? <div className="no-data-text">{props.langText && props.langText.NoDataText ? props.langText.NoDataText : 'データがありません'}</div>
                    : null)
            }
            </div>
            {
                props.showPagination &&
                <div className="pagination">
                    <Button variant="outline-secondary" className="page-btn" onClick={() => gotoPage(0)} disabled={!canPreviousPage}>
                        {"<<"}
                    </Button>
                    <Button variant="outline-secondary" className="page-btn" onClick={() => previousPage()} disabled={!canPreviousPage}>
                        {"<"}
                    </Button>
                    <span className="page-span">
                        <strong>
                        <input
                            className="page-input"
                            type="number"
                            min={1}
                            max={pageOptions.length > 0 ? pageOptions.length : 1}
                            value={pageInputValue}
                            onChange={(e) => setPageInputValue(e.target.value)}
                            onBlur={commitPageInput}
                            onKeyDown={(e) => {
                                if (e.key === "Enter") {
                                    e.preventDefault();
                                    commitPageInput();
                                }
                            }}
                            // Issue #452: ホイールでの誤操作（意図しないページ送り）を無効化する
                            onWheel={(e) => e.target.blur()}
                            style={{ width: "100px" }}
                        />
                        / {pageOptions.length}
                        </strong>
                    </span>
                    {
                        props.showPageSize&&
                        <select
                            value={pageSize}
                            onChange={(e) => {
                                setPageSize(Number(e.target.value));
                            }}
                        >
                            {[5, 10, 20, 50, 100].map((pageSize) => (
                                <option key={pageSize} value={pageSize}>
                                    {pageSize}{props.langText.RowsText}
                                </option>
                            ))}
                        </select>
                    }

                    <Button variant="outline-secondary" className="page-btn" onClick={() => previousPage()} disabled={!canPreviousPage}>
                        {"<"}
                    </Button>
                    <Button variant="outline-secondary" className="page-btn" onClick={() => nextPage()} disabled={!canNextPage}>
                        {">"}
                    </Button>
                    <Button variant="outline-secondary" className="page-btn" onClick={() => gotoPage(pageCount - 1)} disabled={!canNextPage}>
                        {">>"}
                    </Button>
                </div>
            }
        </>
    )
}
export default ({
                    columns,
                    data,
                    defaultPageSize = 20,
                    page,
                    ...props
                }
) => {

    return (
        <Table columns={columns}
               data ={data}
               page={page}
               defaultPageSize={defaultPageSize}
               state = {props.state}
               pageCount={props.pageCount}
               loading = {props.loading}
               manualPagination = {props.manual}
               {...props} />
    )
};